import axios from 'axios';
import { ADD_PROMPT_SECTION_DATA, DELETE_PROMPT_SECTION_DATA, FETCH_AI_THEME_REQUEST, FETCHING_AI_THEME_FAILED, FETCHING_AI_THEME_SUCCEEDED, PROMPT_EDITOR_SECTION_CANCEL, PROMPT_EDITOR_SECTION_FAILED, PROMPT_EDITOR_SECTION_MESSAGE, 
         PROMPT_EDITOR_SECTION_REQUEST, PROMPT_EDITOR_SECTION_SUCCEEDED, 
         RESET_PROMPT_CREATION_FLOW,
         UPDATE_PROMPT_CREATION_DATA, UPDATE_PROMPT_SECTION_DATA,
         UPDATE_PROMPT_SECTION_ORDER_DATA} from 'constants/actionTypes';
import buildUrl from 'build-url';
import { createAction } from 'redux-actions';
import { END, eventChannel } from 'redux-saga';
import { call, put, take, takeEvery, takeLatest } from 'redux-saga/effects';
import AuthService from 'services/auth.service';

const apiUrl = process.env.REACT_APP_CLEVERSTORY_PYTHON_SERVICE_URL;
const PROMPT_EDITOR_URL = `${apiUrl}/ai-builder`;


// ============================================================================
// SSE Channel and Stream Handler
// ============================================================================

export function createSseChannel(url, body, headers = {}, workspace) {
  return eventChannel((emit) => {
    const controller = new AbortController();

    (async () => {
      try {
        const isFormData = body instanceof FormData;

        const fetchHeaders = {
          Accept: 'text/event-stream',
          ...headers,
        };

        if (AuthService.isAuthenticated()) {
          const token = AuthService.getAccessToken();
          if (token) {
            fetchHeaders.Authorization = `Bearer ${token}`;
          }
        }

        if (workspace) {
          fetchHeaders['cs-workspace-id'] = workspace;
        }

        if (!isFormData) {
          fetchHeaders['Content-Type'] = 'application/json';
        }

        const response = await fetch(url, {
          method: 'POST',
          headers: fetchHeaders,
          body: isFormData ? body : JSON.stringify(body),
          signal: controller.signal,
          credentials: 'include'
        });

        if (!response.ok) {
          const text = await response.text();
          console.error('Request failed:', response.status, text); // ← Debug
          emit({ type: 'error', error: new Error(`${response.status}: ${text}`) });
          emit(END);
          return;
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder('utf-8');
        let buffer = '';

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          const chunks = buffer.split('\n\n');
          buffer = chunks.pop();

          for (const chunk of chunks) {
            let eventType = 'message';
            let data = '';

            chunk.split('\n').forEach((line) => {
              if (line.startsWith('event:')) {
                eventType = line.replace('event:', '').trim();
              }
              if (line.startsWith('data:')) {
                data += line.replace('data:', '').trim();
              }
            });

            if (!data) continue;

            try {
              emit({ type: eventType, data: JSON.parse(data) });
            } catch {
              emit({ type: eventType, data });
            }
          }
        }

        emit({ type: 'complete' });
        emit(END);
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('SSE Error:', error);
          emit({ type: 'error', error });
        }
        emit(END);
      }
    })();

    return () => controller.abort();
  });
}

export function* handleSseStream({
  url,
  body,
  messageAction,
  successAction,
  errorAction,
  workspace,
  callback,
  headers,
  editorSession,
  widgetId,
}) {
  const channel = yield call(createSseChannel, url, body, headers, workspace);

  try {
    while (true) {
      const event = yield take(channel);

      if (event.type === "message" && messageAction) {
        // Streaming message
        yield put(messageAction(event.data, editorSession, widgetId));
      } else if (event.type === "complete") {
        // Stream complete
        yield put(successAction(event.data, editorSession, widgetId));
        if (callback) callback(event.data);
        break;
      } else if (event.type === "error") {
        // Stream error
        yield put(errorAction(event.error, editorSession, widgetId));
        break;
      }
    }
  } finally {
    console.log(`SSE closed for ${url}`);
  }
}


// ============================================================================
// SECTION
// ============================================================================

// export const promptEditorSectionRequest = createAction(
//   PROMPT_EDITOR_SECTION_REQUEST,
//   (request, workspace, callback) => ({ request, workspace, callback })
// );

// export const promptEditorSectionMessage = createAction(
//   PROMPT_EDITOR_SECTION_MESSAGE,
//   (message, editorSession, widgetId) => ({ message, editorSession, widgetId })
// );

// export const promptEditorSectionSucceeded = createAction(
//   PROMPT_EDITOR_SECTION_SUCCEEDED,
//   (data, editorSession, widgetId) => ({ data, editorSession, widgetId })
// );

// export const promptEditorSectionFailed = createAction(
//   PROMPT_EDITOR_SECTION_FAILED,
//   (error, editorSession, widgetId) => ({ error, editorSession, widgetId })
// );

// export const promptEditorSectionCancel = createAction(
//   PROMPT_EDITOR_SECTION_CANCEL
// );

// function* promptEditorSectionSaga(action) {
//   const { request, workspace, callback } = action.payload;

//   const url = `${PROMPT_EDITOR_URL}/section`;

//   yield call(handleSseStream, {
//     url,
//     body: request,
//     messageAction: promptEditorSectionMessage,
//     successAction: promptEditorSectionSucceeded,
//     errorAction: promptEditorSectionFailed,
//     workspace,
//     callback,
//   });
// }


export const promptEditorSectionRequest = createAction(PROMPT_EDITOR_SECTION_REQUEST, (request, workspace) => {
  return {
    request: request,
    workspace: workspace
  };
});

const promptEditorSectionSucceeded = createAction(PROMPT_EDITOR_SECTION_SUCCEEDED, (response) => {
  return {
    response: response
  };
});

const promptEditorSectionFailed = createAction(PROMPT_EDITOR_SECTION_FAILED, (error) => {
  return {
    error: error
  }
});

function* promptEditorSectionSaga(action) {
  const { request, workspace } = action.payload;

  let url = buildUrl(`${PROMPT_EDITOR_URL}/section`, {});

  const promptEditorSectionAPI = () => {
    return axios.post(url, request, {
      headers: {
        'CS-WORKSPACE-ID': workspace
      }
    }).then(response => response.data);
  }

  try {
    const response = yield call(promptEditorSectionAPI);
    if (response) {
      yield put(promptEditorSectionSucceeded(response));
    }
  } catch (error) {
    yield put(promptEditorSectionFailed(error));
  }
}

/**
 * Fetch AI Theme
*/
export const fetchAiTheme = createAction(FETCH_AI_THEME_REQUEST, (callback) => {
  return {
    callback: callback
  };
});

export const fetchAiThemeSucceeded = createAction(FETCHING_AI_THEME_SUCCEEDED, (response) => {
  return response;
});

export const fetchAiThemeFailed = createAction(FETCHING_AI_THEME_FAILED, (error) => {
  return error;
});

function* fetchAiThemeSaga(action) {
  const { callback } = action.payload;

  const url = buildUrl(apiUrl, {
    path: "/cleverstory/1.0/ai-theme",
  });

  const fetchAiThemeAPI = () => axios.get(url).then((response) => response.data);

  try {
    const response = yield call(fetchAiThemeAPI);
    if (response) {
      yield put(fetchAiThemeSucceeded(response));
      if (callback) callback();
    }
  } catch (error) {
    yield put(fetchAiThemeFailed(error));
  }
}

/**
 * Update Prompt Creation Data
*/
export const updatePromptSectionOrderData = createAction(UPDATE_PROMPT_SECTION_ORDER_DATA, (data) => {
  return data;
});

export const updatePromptCreationData = createAction(UPDATE_PROMPT_CREATION_DATA, (key, value) => {
  return {
    key,
    value,
  };
});


export const addPromptSectionData = createAction(ADD_PROMPT_SECTION_DATA, (data, id) => {
  return {
    data: data,
    id: id
  };
});

/**
 * Delete Prompt Section Data
*/
export const deletePromptSectionData = createAction(DELETE_PROMPT_SECTION_DATA, (id) => {
  return {
    id: id
  };
});

/**
 * Update Prompt Section Data
*/
export const updatePromptSectionData = createAction(UPDATE_PROMPT_SECTION_DATA, (id, key, value) => {
  return {
    id,
    key,
    value,
  };
});


/**
 * Reset Prompt Creation Flow
*/
export const resetPromptCreationFlow = createAction(RESET_PROMPT_CREATION_FLOW);

export default function* promptEditorSaga() {
  yield takeLatest(PROMPT_EDITOR_SECTION_REQUEST, promptEditorSectionSaga);
  yield takeEvery(FETCH_AI_THEME_REQUEST, fetchAiThemeSaga);
}